import axios from "axios";
import { API_URL } from "../constants";

/**
 * Personalas → «Darbuotojai» list and the «darbuotojo kortelė» —
 * erp-backend `GET /:companyId/personnel` and
 * `GET /:companyId/personnel/:userId/summary`, contract in
 * docs/prd/employee-work-card.md («Phase 1 — implementation spec → Backend»).
 *
 * Shapes as confirmed against erp-backend api/controllers/personnel/{list,summary}.js.
 * The normalisers below only guard against missing optional fields and keep
 * two distinctions the screens rely on:
 *   - `employmentTerms.salary` is omitted when the viewer may not see it;
 *     present-but-null means "not recorded";
 *   - dates may come as a timestamp (ms) or a date string — kept as given and
 *     formatted by `formatDay` in the feature helpers.
 * Bare axios, like every other service here — the global interceptor already
 * attaches credentials.
 */

export type PersonnelStatus = "active" | "left";

export type DateValue = string | number | null;

export interface PersonnelRef {
  id: string;
  name: string;
}

export interface PersonnelChips {
  /** Unsigned documents of the person's active pareigybės («Tvarkos»). */
  wsUnsignedCount: number;
  /** Pareigybės still waiting for a sphere's approval. */
  pendingApprovals: number;
  medicalValidUntil: DateValue;
  jobLeaveStatus: string | null;
  isPrivate: boolean;
  /** false → this person has no hiring («Įdarbinimas») record. Missing on an
   * older backend is treated as true, so it never shows a false marker. */
  hasOnboardingRecord: boolean;
}

export interface PersonnelRow {
  userId: string;
  name: string;
  avatarFileId: string | null;
  positionsText: string;
  departments: PersonnelRef[];
  manager: PersonnelRef | null;
  hiringDate: DateValue;
  /** From the person's job leave (completed one for «Išėję»); null when there is none. */
  quitDate: DateValue;
  chips: PersonnelChips;
}

export interface PersonnelListResponse {
  rows: PersonnelRow[];
  total: number;
  page: number;
  limit: number;
}

export interface PersonnelListFilters {
  status: PersonnelStatus;
  search?: string;
  departmentId?: string | null;
  positionId?: string | null;
  /** "missing" → only people without a hiring record (applied before paging). */
  onboardingRecord?: "missing" | null;
  page?: number;
  limit?: number;
}

export interface PersonnelPosition {
  id: string;
  title: string;
}

export interface PersonnelPerson {
  userId: string;
  name: string;
  email: string | null;
  phone: string | null;
  companyPhone: string | null;
  avatarFileId: string | null;
  /** Only sent to Master / 'Access onboarding'. */
  gender: string | null;
  /** Hiring date of the current record — sent to every viewer of the card, also when the terms are not. */
  hiringDate: DateValue;
  /**
   * Asmens kodas of this company's membership. Absent (undefined) when the
   * viewer may not see it; null = not recorded.
   */
  personalCode?: string | null;
}

export type PersonnelEmploymentStatus = "active" | "left" | "hiring";

export interface PersonnelMembership {
  isDisabled: boolean;
  isActivated: boolean;
  isPrivate: boolean;
  isUserGuest: boolean;
  /** CompaniesUsers id — the subjectId of this person's UserMembership audit entries. */
  companiesUserId: string | null;
  employmentStatus: PersonnelEmploymentStatus | null;
}

export interface PersonnelOnboardingRef {
  id: string;
  mode: string | null;
  status: string | null;
  hiringDate: DateValue;
  createdAt: DateValue;
}

export interface PersonnelEmploymentTerms {
  /** The onboarding record these terms come from; null without HR reach (or when unknown). */
  onboardingId: string | null;
  contractType: string | null;
  contractVariant: string | null;
  originalContractNumber: string | null;
  hiringDate: DateValue;
  contractEndDate: DateValue;
  probationMonths: number | null;
  hoursPerWeek: number | null;
  /** Absent (undefined) when the viewer may not see salaries. */
  salary?: number | string | null;
  /** The already-scheduled second salary (docs/prd/probation-salary-review.md §5) — absent when the viewer may not see salaries, null when there is none or it is no longer ahead. */
  plannedSalaryChange?: { date: string | number; amount: number } | null;
  /**
   * The «Bandomasis laikotarpis» tile (backend personnel/summary.js ~L194-201
   * nests it here, not at the top level) — absent or null both mean "no
   * probation review applies" (not HR/head reach, no current probation, the
   * viewer is the employee themself, etc).
   */
  probation?: PersonnelProbation | null;
}

export type ProbationReviewStatus = "pending" | "passed" | "failed" | "passed_unreviewed" | "cancelled";

export interface PersonnelProbation {
  endDate: DateValue;
  status: ProbationReviewStatus;
  decisionDeadline: DateValue;
  noticeDeadline: DateValue;
}

export interface PersonnelAmendmentRef {
  id: string;
  onboardingId: string | null;
  status: string | null;
  effectiveDate: DateValue;
  isEmploymentTermsChange: boolean;
}

export interface PersonnelViewerCan {
  edit: boolean;
  /** May change the asmens kodas — Master / 'Access onboarding'. */
  editPersonalCode: boolean;
  manageOnboarding: boolean;
  medical: boolean;
  workSecurity: boolean;
  jobLeave: boolean;
  audit: boolean;
  /** «Sutartis ir pakeitimai» — HR within reach (Master / 'Access onboarding'). */
  amendments: boolean;
  /** «Atostogos» — the person's leave requests. */
  leaves: boolean;
  /** «Turtas» — assets issued to the person. */
  assets: boolean;
  /** «Komandiruotės» — Master | 'Access business trips' | head over the person. */
  businessTrips: boolean;
  /** «Kuro ataskaitos» — Master | 'Access fuel reports' | head over the person. */
  fuelReports: boolean;
  /** «Išlaidos» — Master | 'Access fuel reports' (no separate expenses permission) | head over the person. */
  expenses: boolean;
  /** Probation review (docs/prd/probation-salary-review.md §5) — line manager, HR in scope, or a head (view only). */
  probationReview: boolean;
}

export interface PersonnelSummary {
  person: PersonnelPerson;
  positions: PersonnelPosition[];
  positionsText: string;
  departments: PersonnelRef[];
  manager: PersonnelRef | null;
  membership: PersonnelMembership;
  /** Newest first. */
  onboardings: PersonnelOnboardingRef[];
  employmentTerms: PersonnelEmploymentTerms | null;
  amendments: PersonnelAmendmentRef[];
  /** `id` is null for viewers without HR reach — the status still shows, the leave view does not. */
  jobLeave: { id: string | null; status: string | null } | null;
  /** status 'pending' = a renewal is open. */
  medical: { validUntil: DateValue; status: string | null } | null;
  workSecurity: { requiredUnsignedCount: number; unsignedCount: number; pendingApprovals: number } | null;
  viewerCan: PersonnelViewerCan;
}

const str = (value: unknown): string => (value == null ? "" : String(value));
const strOrNull = (value: unknown): string | null =>
  value == null || value === "" ? null : String(value);
const num = (value: unknown): number => Number(value) || 0;
const numOrNull = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const dateOrNull = (value: unknown): DateValue =>
  typeof value === "number" || (typeof value === "string" && value !== "") ? value : null;
const arr = (value: unknown): any[] => (Array.isArray(value) ? value : []);

function normalizeRef(raw: any): PersonnelRef | null {
  if (!raw) return null;
  if (typeof raw === "string") return { id: raw, name: "" };
  const id = str(raw.id ?? raw.userId);
  if (!id && !raw.name) return null;
  return { id, name: str(raw.name) };
}

function normalizeRefs(raw: unknown): PersonnelRef[] {
  return arr(raw)
    .map(normalizeRef)
    .filter((ref): ref is PersonnelRef => ref !== null);
}

/** One list row; exported for the mapping tests. */
export function normalizePersonnelRow(raw: any): PersonnelRow {
  const chips = raw?.chips || {};
  return {
    userId: str(raw?.userId ?? raw?.id),
    name: str(raw?.name),
    avatarFileId: strOrNull(raw?.avatarFileId),
    positionsText: str(raw?.positionsText),
    departments: normalizeRefs(raw?.departments),
    manager: normalizeRef(raw?.manager),
    hiringDate: dateOrNull(raw?.hiringDate),
    quitDate: dateOrNull(raw?.quitDate),
    chips: {
      wsUnsignedCount: num(chips.wsUnsignedCount),
      pendingApprovals: num(chips.pendingApprovals),
      medicalValidUntil: dateOrNull(chips.medicalValidUntil),
      jobLeaveStatus: strOrNull(chips.jobLeaveStatus),
      isPrivate: !!chips.isPrivate,
      hasOnboardingRecord: chips.hasOnboardingRecord !== false,
    },
  };
}

export function normalizePersonnelList(data: any, fallback: { page: number; limit: number }): PersonnelListResponse {
  const rows = arr(data?.rows).map(normalizePersonnelRow);
  return {
    rows,
    total: data?.total != null ? num(data.total) : rows.length,
    page: numOrNull(data?.page) || fallback.page,
    limit: numOrNull(data?.limit) || fallback.limit,
  };
}

function normalizePosition(raw: any): PersonnelPosition | null {
  if (!raw) return null;
  if (typeof raw === "string") return { id: raw, title: "" };
  return { id: str(raw.id), title: str(raw.title) };
}

function normalizeTerms(raw: any): PersonnelEmploymentTerms | null {
  if (!raw || typeof raw !== "object") return null;
  const terms: PersonnelEmploymentTerms = {
    onboardingId: strOrNull(raw.onboardingId),
    contractType: strOrNull(raw.contractType),
    contractVariant: strOrNull(raw.contractVariant),
    originalContractNumber: strOrNull(raw.originalContractNumber),
    hiringDate: dateOrNull(raw.hiringDate),
    contractEndDate: dateOrNull(raw.contractEndDate),
    probationMonths: numOrNull(raw.probationMonths),
    hoursPerWeek: numOrNull(raw.hoursPerWeek),
  };
  // Keep "not sent" (no right to see it) apart from "sent but empty".
  if (Object.prototype.hasOwnProperty.call(raw, "salary")) {
    terms.salary = raw.salary == null || raw.salary === "" ? null : raw.salary;
  }
  if (Object.prototype.hasOwnProperty.call(raw, "plannedSalaryChange")) {
    const change = raw.plannedSalaryChange;
    terms.plannedSalaryChange =
      change && change.date != null && change.amount != null
        ? { date: change.date, amount: Number(change.amount) }
        : null;
  }
  // Unlike salary, an absent key here is not a distinct "no right to see it"
  // state (the backend only ever omits it OR sends null — both mean no
  // review applies) — so it is fine to always normalize to null.
  terms.probation = normalizeProbation(raw.probation);
  return terms;
}

function normalizeProbation(raw: any): PersonnelProbation | null {
  if (!raw || typeof raw !== "object") return null;
  const status: ProbationReviewStatus = ["pending", "passed", "failed", "passed_unreviewed", "cancelled"].includes(
    raw.status
  )
    ? raw.status
    : "pending";
  return {
    endDate: dateOrNull(raw.endDate),
    status,
    decisionDeadline: dateOrNull(raw.decisionDeadline),
    noticeDeadline: dateOrNull(raw.noticeDeadline),
  };
}

function normalizePerson(raw: any, userId: string): PersonnelPerson {
  const person: PersonnelPerson = {
    userId: str(raw.userId ?? raw.id ?? userId) || userId,
    name: str(raw.name),
    email: strOrNull(raw.email),
    phone: strOrNull(raw.phone),
    companyPhone: strOrNull(raw.companyPhone),
    avatarFileId: strOrNull(raw.avatarFileId),
    gender: strOrNull(raw.gender),
    hiringDate: dateOrNull(raw.hiringDate),
  };
  // Keep "not sent" (no right to see it) apart from "sent but empty".
  if (Object.prototype.hasOwnProperty.call(raw, "personalCode")) person.personalCode = strOrNull(raw.personalCode);
  return person;
}

/** The summary answer; exported for the mapping tests. */
export function normalizePersonnelSummary(data: any, userId: string): PersonnelSummary {
  const raw = data || {};
  const person = raw.person || {};
  const membership = raw.membership || {};
  const viewerCan = raw.viewerCan || {};
  const positions = arr(raw.positions)
    .map(normalizePosition)
    .filter((position): position is PersonnelPosition => position !== null);
  const ws = raw.workSecurity;
  return {
    person: normalizePerson(person, userId),
    positions,
    positionsText:
      str(raw.positionsText) ||
      positions
        .map((position) => position.title)
        .filter(Boolean)
        .join(", "),
    departments: normalizeRefs(raw.departments),
    manager: normalizeRef(raw.manager),
    membership: {
      isDisabled: !!membership.isDisabled,
      isActivated: !!membership.isActivated,
      isPrivate: !!membership.isPrivate,
      isUserGuest: !!membership.isUserGuest,
      companiesUserId: strOrNull(membership.companiesUserId),
      employmentStatus: ["active", "left", "hiring"].includes(membership.employmentStatus)
        ? membership.employmentStatus
        : null,
    },
    onboardings: arr(raw.onboardings).map((item: any) => ({
      id: str(item?.id),
      mode: strOrNull(item?.mode),
      status: strOrNull(item?.status),
      hiringDate: dateOrNull(item?.hiringDate),
      createdAt: dateOrNull(item?.createdAt),
    })),
    employmentTerms: normalizeTerms(raw.employmentTerms),
    amendments: arr(raw.amendments).map((item: any) => ({
      id: str(item?.id),
      onboardingId: strOrNull(item?.onboardingId),
      status: strOrNull(item?.status),
      effectiveDate: dateOrNull(item?.effectiveDate),
      isEmploymentTermsChange: !!item?.isEmploymentTermsChange,
    })),
    jobLeave: raw.jobLeave ? { id: strOrNull(raw.jobLeave.id), status: strOrNull(raw.jobLeave.status) } : null,
    medical: raw.medical
      ? { validUntil: dateOrNull(raw.medical.validUntil), status: strOrNull(raw.medical.status) }
      : null,
    workSecurity: ws
      ? {
          requiredUnsignedCount: num(ws.requiredUnsignedCount),
          unsignedCount: num(ws.unsignedCount),
          pendingApprovals: num(ws.pendingApprovals),
        }
      : null,
    // A missing flag means "no" — never show a tab the backend did not allow.
    viewerCan: {
      edit: viewerCan.edit === true,
      editPersonalCode: viewerCan.editPersonalCode === true,
      manageOnboarding: viewerCan.manageOnboarding === true,
      medical: viewerCan.medical === true,
      workSecurity: viewerCan.workSecurity === true,
      jobLeave: viewerCan.jobLeave === true,
      audit: viewerCan.audit === true,
      amendments: viewerCan.amendments === true,
      leaves: viewerCan.leaves === true,
      assets: viewerCan.assets === true,
      businessTrips: viewerCan.businessTrips === true,
      fuelReports: viewerCan.fuelReports === true,
      expenses: viewerCan.expenses === true,
      probationReview: viewerCan.probationReview === true,
    },
  };
}

export const PERSONNEL_PAGE_SIZE = 50;

/**
 * Query string for the list. `status` is always sent (the backend defaults to
 * active, but being explicit keeps «Išėję» from silently falling back);
 * empty filters are left out; page/limit only when they differ from the
 * backend defaults (1 / 50).
 */
export function buildPersonnelQuery(filters: PersonnelListFilters): string {
  const params = new URLSearchParams();
  params.set("status", filters.status === "left" ? "left" : "active");
  const search = (filters.search || "").trim();
  if (search) params.set("search", search);
  if (filters.departmentId) params.set("departmentId", filters.departmentId);
  if (filters.positionId) params.set("positionId", filters.positionId);
  if (filters.onboardingRecord === "missing") params.set("onboardingRecord", "missing");
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.limit && filters.limit !== PERSONNEL_PAGE_SIZE) params.set("limit", String(filters.limit));
  return params.toString();
}

/** GET /:companyId/personnel */
export async function fetchPersonnel(
  companyId: string,
  filters: PersonnelListFilters
): Promise<PersonnelListResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel?${buildPersonnelQuery(filters)}`);
  return normalizePersonnelList(res.data, {
    page: filters.page || 1,
    limit: filters.limit || PERSONNEL_PAGE_SIZE,
  });
}

/** GET /:companyId/personnel/:userId/summary — 403 unless the viewer may open this card. */
export async function fetchPersonnelSummary(companyId: string, userId: string): Promise<PersonnelSummary> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel/${userId}/summary`);
  return normalizePersonnelSummary(res.data, userId);
}

/**
 * PATCH /:companyId/personnel/:userId/personal-code — Master / 'Access
 * onboarding'. null clears; a code failing the LT check is a 400
 * INVALID_PERSONAL_CODE. Resolves to the stored code.
 */
export async function updatePersonalCode(
  companyId: string,
  userId: string,
  personalCode: string | null
): Promise<string | null> {
  const res = await axios.patch(`${API_URL}/${companyId}/personnel/${userId}/personal-code`, { personalCode });
  return strOrNull(res.data?.personalCode);
}

/**
 * «Turtas» — `GET /:companyId/personnel/:userId/assets` →
 * `{ current, history, canOpenAssets }` (phase 2, docs/prd/employee-work-card.md § C):
 *   - current[] `{ assetId, name, brand, model, serial, type: {id,title}|null,
 *     status, isBroken, issuedDate: 'YYYY-MM-DD'|null, assignAct, returnAct }`;
 *   - history[] `{ historyId, assetId, name, brand, model, serial, type,
 *     issuedDate, returnedDate|null, isCurrent, assignAct, returnAct }`;
 *   - act `{ status: 'signed'|'pending'|'annulled', createdAt, fileId? }` —
 *     `fileId` only when `canOpenAssets` (Master / 'Access assets').
 * Every field is treated as optional; file ids are not read — the card shows
 * names, dates and act status only, and links into the Assets module when
 * `canOpenAssets`.
 */
export interface PersonnelAssetAct {
  status: string | null;
  createdAt: DateValue;
}

export interface PersonnelAsset {
  /** CompaniesAssets id — the Assets module route `/assets/:assetId`. */
  id: string;
  name: string;
  typeTitle: string;
  brand: string;
  model: string;
  serial: string;
  issuedDate: DateValue;
  status: string | null;
  isBroken: boolean;
  assignAct: PersonnelAssetAct | null;
  returnAct: PersonnelAssetAct | null;
}

export interface PersonnelAssetHistoryRow {
  id: string;
  assetId: string | null;
  name: string;
  typeTitle: string;
  brand: string;
  model: string;
  serial: string;
  issuedDate: DateValue;
  returnedDate: DateValue;
  isCurrent: boolean;
  assignAct: PersonnelAssetAct | null;
  returnAct: PersonnelAssetAct | null;
}

export interface PersonnelAssets {
  current: PersonnelAsset[];
  history: PersonnelAssetHistoryRow[];
  /** Master / 'Access assets' — links into the Assets module. */
  canOpenAssets: boolean;
}

const obj = (value: unknown): Record<string, any> =>
  value && typeof value === "object" ? (value as Record<string, any>) : {};

function typeTitleOf(raw: Record<string, any>): string {
  if (raw.type && typeof raw.type === "object") return str(raw.type.title ?? raw.type.name);
  return typeof raw.type === "string" ? raw.type : "";
}

function normalizeAct(value: unknown): PersonnelAssetAct | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, any>;
  return { status: strOrNull(raw.status), createdAt: dateOrNull(raw.createdAt) };
}

/** One currently issued asset; exported for the mapping tests. */
export function normalizePersonnelAsset(value: unknown): PersonnelAsset | null {
  const raw = obj(value);
  const id = str(raw.assetId ?? raw.id);
  if (!id) return null;
  return {
    id,
    name: str(raw.name),
    typeTitle: typeTitleOf(raw),
    brand: str(raw.brand),
    model: str(raw.model),
    serial: str(raw.serial),
    issuedDate: dateOrNull(raw.issuedDate),
    status: strOrNull(raw.status),
    isBroken: !!raw.isBroken,
    assignAct: normalizeAct(raw.assignAct),
    returnAct: normalizeAct(raw.returnAct),
  };
}

/** One issue/return history entry; exported for the mapping tests. */
export function normalizePersonnelAssetHistory(value: unknown, index = 0): PersonnelAssetHistoryRow {
  const raw = obj(value);
  const assetId = strOrNull(raw.assetId);
  return {
    id: str(raw.historyId ?? raw.id) || `${assetId || "row"}-${index}`,
    assetId,
    name: str(raw.name),
    typeTitle: typeTitleOf(raw),
    brand: str(raw.brand),
    model: str(raw.model),
    serial: str(raw.serial),
    issuedDate: dateOrNull(raw.issuedDate),
    returnedDate: dateOrNull(raw.returnedDate),
    isCurrent: !!raw.isCurrent,
    assignAct: normalizeAct(raw.assignAct),
    returnAct: normalizeAct(raw.returnAct),
  };
}

export function normalizePersonnelAssets(data: unknown): PersonnelAssets {
  const raw = obj(data);
  return {
    current: arr(raw.current)
      .map(normalizePersonnelAsset)
      .filter((item): item is PersonnelAsset => item !== null),
    history: arr(raw.history).map(normalizePersonnelAssetHistory),
    canOpenAssets: raw.canOpenAssets === true,
  };
}

/** GET /:companyId/personnel/:userId/assets — 403 unless the viewer may see this person's assets. */
export async function fetchPersonnelAssets(companyId: string, userId: string): Promise<PersonnelAssets> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel/${userId}/assets`);
  return normalizePersonnelAssets(res.data);
}

/**
 * «Komandiruotės» / «Kuro ataskaitos» / «Išlaidos» (phase 3,
 * docs/prd/employee-work-card.md § Phase 3 — Backend): three read-only,
 * paginated endpoints, each `{ rows, total, page, limit }` like the
 * employees list. `fileId` is present only for a viewer who holds the
 * module's permission or is Master (a head over the person never gets the
 * key at all) — its mere presence is what gates the document-view button,
 * same as `canOpenAssets` gates the assets tab's links.
 */
export interface PersonnelPagedRows<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
}

function normalizePersonnelPage<T>(
  data: any,
  mapRow: (raw: unknown) => T | null,
  fallback: { page: number; limit: number }
): PersonnelPagedRows<T> {
  const rows = arr(data?.rows)
    .map(mapRow)
    .filter((row): row is T => row !== null);
  return {
    rows,
    total: data?.total != null ? num(data.total) : rows.length,
    page: numOrNull(data?.page) || fallback.page,
    limit: numOrNull(data?.limit) || fallback.limit,
  };
}

/** Only sent when the viewer holds the module permission (or is Master); absent (or null) otherwise. */
const fileIdOf = (raw: Record<string, any>): string | null => strOrNull(raw.fileId);

export interface PersonnelBusinessTrip {
  id: string;
  number: string | null;
  destination: string;
  purpose: string;
  startDate: DateValue;
  endDate: DateValue;
  status: string | null;
  /** null | 'pending' | 'signed' | 'rejected' | 'annulled' — the trip report's own workflow. */
  reportStatus: string | null;
  fileId: string | null;
}

/** One business trip row; exported for the mapping tests. */
export function normalizePersonnelBusinessTrip(value: unknown): PersonnelBusinessTrip | null {
  const raw = obj(value);
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    number: strOrNull(raw.number),
    destination: str(raw.destination),
    purpose: str(raw.purpose),
    startDate: dateOrNull(raw.startDate),
    endDate: dateOrNull(raw.endDate),
    status: strOrNull(raw.status),
    reportStatus: strOrNull(raw.reportStatus),
    fileId: fileIdOf(raw),
  };
}

export function normalizePersonnelBusinessTrips(
  data: unknown,
  fallback: { page: number; limit: number }
): PersonnelPagedRows<PersonnelBusinessTrip> {
  return normalizePersonnelPage(data, normalizePersonnelBusinessTrip, fallback);
}

/** GET /:companyId/personnel/:userId/business-trips — 403 unless the viewer may see this person's trips. */
export async function fetchPersonnelBusinessTrips(
  companyId: string,
  userId: string,
  page = 1,
  limit = PERSONNEL_PAGE_SIZE
): Promise<PersonnelPagedRows<PersonnelBusinessTrip>> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel/${userId}/business-trips?${buildTabPageQuery(page, limit)}`);
  return normalizePersonnelBusinessTrips(res.data, { page, limit });
}

export interface PersonnelFuelReport {
  id: string;
  period: string;
  asset: PersonnelRef | null;
  km: number | null;
  /** Fuel consumed in the month (start tank + purchases − end tank) — as printed on the report's PDF. */
  consumedLitres: number | null;
  amount: number | null;
  /** 'draft' | 'pending' | 'signed' | 'annulled' */
  status: string | null;
  fileId: string | null;
}

/** One fuel report row; exported for the mapping tests. */
export function normalizePersonnelFuelReport(value: unknown): PersonnelFuelReport | null {
  const raw = obj(value);
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    period: str(raw.period),
    asset: normalizeRef(raw.asset),
    km: numOrNull(raw.km),
    consumedLitres: numOrNull(raw.consumedLitres),
    amount: numOrNull(raw.amount),
    status: strOrNull(raw.status),
    fileId: fileIdOf(raw),
  };
}

export function normalizePersonnelFuelReports(
  data: unknown,
  fallback: { page: number; limit: number }
): PersonnelPagedRows<PersonnelFuelReport> {
  return normalizePersonnelPage(data, normalizePersonnelFuelReport, fallback);
}

/** GET /:companyId/personnel/:userId/fuel-reports — 403 unless the viewer may see this person's fuel reports. */
export async function fetchPersonnelFuelReports(
  companyId: string,
  userId: string,
  page = 1,
  limit = PERSONNEL_PAGE_SIZE
): Promise<PersonnelPagedRows<PersonnelFuelReport>> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel/${userId}/fuel-reports?${buildTabPageQuery(page, limit)}`);
  return normalizePersonnelFuelReports(res.data, { page, limit });
}

export interface PersonnelExpense {
  id: string;
  date: DateValue;
  /** 'fuel' | 'other' */
  category: string | null;
  amount: number | null;
  currency: string | null;
  status: string | null;
  fileId: string | null;
}

/** One manual expense row; exported for the mapping tests. */
export function normalizePersonnelExpense(value: unknown): PersonnelExpense | null {
  const raw = obj(value);
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    date: dateOrNull(raw.date),
    category: strOrNull(raw.category),
    amount: numOrNull(raw.amount),
    currency: strOrNull(raw.currency),
    status: strOrNull(raw.status),
    fileId: fileIdOf(raw),
  };
}

export function normalizePersonnelExpenses(
  data: unknown,
  fallback: { page: number; limit: number }
): PersonnelPagedRows<PersonnelExpense> {
  return normalizePersonnelPage(data, normalizePersonnelExpense, fallback);
}

/** GET /:companyId/personnel/:userId/expenses — 403 unless the viewer may see this person's expenses. */
export async function fetchPersonnelExpenses(
  companyId: string,
  userId: string,
  page = 1,
  limit = PERSONNEL_PAGE_SIZE
): Promise<PersonnelPagedRows<PersonnelExpense>> {
  const res = await axios.get(`${API_URL}/${companyId}/personnel/${userId}/expenses?${buildTabPageQuery(page, limit)}`);
  return normalizePersonnelExpenses(res.data, { page, limit });
}

/** `page` only past the first, `limit` only away from the shared default — same convention as `buildPersonnelQuery`. */
function buildTabPageQuery(page: number, limit: number): string {
  const params = new URLSearchParams();
  if (page > 1) params.set("page", String(page));
  if (limit !== PERSONNEL_PAGE_SIZE) params.set("limit", String(limit));
  return params.toString();
}
